<script setup lang="ts">
// ================================
// IMPORTS
// ================================
import { computed, ref, watch } from "vue"
import { useRoute } from "vue-router"
import { useI18n } from "vue-i18n"
import { storeToRefs } from "pinia"

import { useBuildingStore } from "@core/stores/building-v2.store"
import { WEATHER_STATION_TYPES_LIST } from "@core/constants"
import type { Source } from "@types"

// ================================
// INTERNATIONALIZATION
// ================================
const { t } = useI18n({
    messages: {
        fr: {
            title: "Station météo",
            empty_message: "Aucune station météo n'est associée à ce bâtiment.",
        },
    },
})

// ================================
// PROPS & EMITS
// ================================
// Aucune props ni emits

// ================================
// COMPOSABLES
// ================================
const route = useRoute()
const buildingStore = useBuildingStore()

const {
    getSources,
    isFetchingBuilding,
    isFetchingBuildingSources,
    getBuildingIsViewOnly,
} = storeToRefs(buildingStore)

// ================================
// ÉTAT
// ================================
/**
 * Identifiant de la source station météo affichée (résolu après chargement des sources)
 */
const idWeatherStation = ref<string>("")

// ================================
// COMPUTED
// ================================
/**
 * Identifiant du bâtiment depuis la route
 */
const getBuildingId = computed<string | undefined>(() => {
    return route.params?._id_building as string | undefined
})

/**
 * Sources du bâtiment correspondant à une station météo
 */
const getWeatherSources = computed<Source[]>(() => {
    const sources = getSources.value ?? []
    return sources.filter((source) => {
        const type = String(source.type ?? source.idtype ?? "")
        return WEATHER_STATION_TYPES_LIST.includes(type)
    })
})

/**
 * Chargement initial bâtiment + sources
 */
const getIsFetching = computed<boolean>(() => {
    return isFetchingBuilding.value || isFetchingBuildingSources.value
})

/**
 * Indique si une station météo est sélectionnée pour affichage
 */
const getHasWeatherStation = computed<boolean>(() => {
    return !!idWeatherStation.value
})

/**
 * Édition réservée aux accès non « partagé lecture seule »
 */
const getWeatherStationEditable = computed<boolean>(() => {
    return Boolean(getBuildingId.value) && !getBuildingIsViewOnly.value
})

// ================================
// INTERACTIONS
// ================================
/**
 * Même ordre que PageBuildingSensorList (stations météo en tête).
 */
function getOrderedWeatherSources(sources: Source[]): Source[] {
    return [...sources].sort((a, b) => {
        const typeA = String(a.type ?? a.idtype ?? "")
        const typeB = String(b.type ?? b.idtype ?? "")
        const isWeatherA = WEATHER_STATION_TYPES_LIST.includes(typeA)
        const isWeatherB = WEATHER_STATION_TYPES_LIST.includes(typeB)
        if (isWeatherA && !isWeatherB) return -1
        if (!isWeatherA && isWeatherB) return 1
        return 0
    })
}

/**
 * Résout l'identifiant de station à partir de la query et des sources disponibles
 */
function resolveWeatherStationId(sources: Source[]): string {
    const ordered = getOrderedWeatherSources(sources)
    const raw = route.query._id_source
    const queryId = Array.isArray(raw) ? raw[0] : raw
    if (typeof queryId === "string" && queryId.length > 0) {
        const match = ordered.find((s) => s._id_source === queryId)
        if (match) return match._id_source
    }
    const first = ordered[0]
    return first?._id_source ?? ""
}

// ================================
// WATCHERS
// ================================
watch(
    [() => route.query._id_source, getWeatherSources],
    () => {
        const sources = getWeatherSources.value
        if (sources.length === 0) {
            idWeatherStation.value = ""
            return
        }
        idWeatherStation.value = resolveWeatherStationId(sources)
    },
    { immediate: true, deep: true }
)
</script>

<template>
    <div class="page page-building-weather-station">
        <!-- ================================ -->
        <!-- HEADER -->
        <!-- ================================ -->
        <HeaderPageBuilding
            :title="t('title')"
            class-header="flex justify-between items-center flex-wrap gap-4"
        />

        <!-- ================================ -->
        <!-- CONTENU -->
        <!-- ================================ -->
        <div class="mt-8">
            <div class="flex flex-col gap-4" v-if="getIsFetching">
                <BaseSkeleton width="100%" height="30rem" />
            </div>

            <WeatherStationDisplay
                v-else-if="getHasWeatherStation && getBuildingId"
                :source-id="idWeatherStation"
                :editable="getWeatherStationEditable"
            />

            <div
                v-else
                class="min-h-80 flex flex-col items-center justify-center gap-4 py-12"
            >
                <p class="text-gray-600 text-center">
                    {{ t("empty_message") }}
                </p>
            </div>
        </div>
    </div>
</template>
